import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="admin-metrics"
export default class extends Controller {
  static targets = [
    "progressChart",
    "hourlyChart",
    "updatedAt",
    "preset",
    "customRange",
    "fromDate",
    "toDate",
    "activeHint",
    "inProgressHint",
    "startedHint",
    "awaitingHint",
    "refreshInterval"
  ]
  static values = {
    url: String,
    interval: { type: Number, default: 10000 },
    preset: { type: String, default: "24h" },
    from: String,
    to: String
  }

  connect() {
    this.charts = {}
    this.waitForChartJs().then(() => {
      this.renderChartsFromDom()
      this.startRefreshTimer()
    })
  }

  disconnect() {
    this.stopRefreshTimer()
    Object.values(this.charts).forEach((chart) => chart.destroy())
    this.charts = {}
  }

  changeRefreshInterval() {
    this.intervalValue = Number(this.refreshIntervalTarget.value)
  }

  intervalValueChanged() {
    this.startRefreshTimer()
  }

  startRefreshTimer() {
    this.stopRefreshTimer()
    if (!this.intervalValue) return

    this.timer = setInterval(() => this.refresh(), this.intervalValue)
  }

  stopRefreshTimer() {
    clearInterval(this.timer)
    this.timer = null
  }

  changePreset() {
    const preset = this.presetTarget.value
    this.presetValue = preset
    this.toggleCustomRange(preset === "custom")

    if (preset === "custom") {
      if (!this.fromDateTarget.value) this.fromDateTarget.value = this.fromValue
      if (!this.toDateTarget.value) this.toDateTarget.value = this.toValue
      this.applyCustomRange()
      return
    }

    this.refresh()
  }

  applyCustomRange() {
    if (this.presetTarget.value !== "custom") return

    const from = this.fromDateTarget.value
    const to = this.toDateTarget.value
    if (!from || !to) return
    if (from > to) return

    this.presetValue = "custom"
    this.fromValue = from
    this.toValue = to
    this.refresh()
  }

  toggleCustomRange(show) {
    this.customRangeTarget.classList.toggle("d-none", !show)
  }

  waitForChartJs() {
    if (window.Chart) return Promise.resolve()

    return new Promise((resolve) => {
      const check = () => {
        if (window.Chart) {
          resolve()
        } else {
          requestAnimationFrame(check)
        }
      }
      check()
    })
  }

  metricsUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("range_preset", this.presetValue || "24h")

    if (this.presetValue === "custom") {
      if (this.fromValue) url.searchParams.set("range_from", this.fromValue)
      if (this.toValue) url.searchParams.set("range_to", this.toValue)
    }

    return url.toString()
  }

  async refresh() {
    try {
      const response = await fetch(this.metricsUrl(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return

      const metrics = await response.json()
      this.syncRangeFromMetrics(metrics)
      this.updateSummary(metrics)
      this.updateProgressChart(metrics)
      this.updateHourlyChart(metrics)
      this.updateTimestamp(metrics.generated_at)
    } catch (error) {
      console.error("Failed to refresh admin metrics", error)
    }
  }

  syncRangeFromMetrics(metrics) {
    if (!metrics.range) return

    this.presetValue = metrics.range.preset || this.presetValue
    this.fromValue = metrics.range.from_date || this.fromValue
    this.toValue = metrics.range.to_date || this.toValue
  }

  updateSummary(metrics) {
    this.element.querySelectorAll("[data-metric]").forEach((node) => {
      const key = node.dataset.metric
      if (Object.prototype.hasOwnProperty.call(metrics, key)) {
        node.textContent = metrics[key]
      }
    })

    this.updateHints()
  }

  updateHints() {
    const label = this.rangeLabel()

    if (this.hasActiveHintTarget) {
      this.activeHintTarget.textContent = "Updated in last 30 min"
    }
    if (this.hasInProgressHintTarget) {
      this.inProgressHintTarget.textContent = `Incomplete in ${label}`
    }
    if (this.hasStartedHintTarget) {
      this.startedHintTarget.textContent = `Started in ${label}`
    }
    if (this.hasAwaitingHintTarget) {
      this.awaitingHintTarget.textContent = `In ${label}, no account yet`
    }
  }

  rangeLabel() {
    switch (this.presetValue) {
      case "1h":
        return "last 1 hour"
      case "today":
        return "today"
      case "7d":
        return "last 7 days"
      case "custom":
        return `${this.fromValue} to ${this.toValue}`
      default:
        return "last 24 hours"
    }
  }

  updateTimestamp(isoString) {
    if (!this.hasUpdatedAtTarget || !isoString) return

    const date = new Date(isoString)
    this.updatedAtTarget.textContent = date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit"
    })
  }

  renderChartsFromDom() {
    const progressData = this.parseJson(this.progressChartTarget.dataset.chart)
    const hourlyData = this.parseJson(this.hourlyChartTarget.dataset.chart)

    this.charts.progress = new window.Chart(this.progressChartTarget, {
      type: "bar",
      data: {
        labels: progressData.labels,
        datasets: [{
          label: "Applications in progress",
          data: progressData.values,
          backgroundColor: "#2554af",
          borderRadius: 6,
          maxBarThickness: 48
        }]
      },
      options: this.barOptions(this.progressChartTitle())
    })

    this.charts.hourly = new window.Chart(this.hourlyChartTarget, {
      type: "line",
      data: {
        labels: hourlyData.labels,
        datasets: [{
          label: "Applications started",
          data: hourlyData.values,
          borderColor: "#40eeb5",
          backgroundColor: "rgba(64, 238, 181, 0.2)",
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          pointHoverRadius: 4
        }]
      },
      options: this.lineOptions(this.hourlyChartTitle())
    })
  }

  progressChartTitle() {
    return `Applications by stage · ${this.rangeLabel()}`
  }

  hourlyChartTitle() {
    const bucket = ["1h", "24h", "today"].includes(this.presetValue) ? "hour" : "day"
    return `Applications started by ${bucket} · ${this.rangeLabel()}`
  }

  updateProgressChart(metrics) {
    const stages = metrics.progress_stages || {}
    const chart = this.charts.progress
    if (!chart) return

    chart.data.labels = Object.keys(stages)
    chart.data.datasets[0].data = Object.values(stages)
    chart.options.plugins.title.text = this.progressChartTitle()
    chart.update("none")
  }

  updateHourlyChart(metrics) {
    const hours = metrics.hourly_started || []
    const chart = this.charts.hourly
    if (!chart) return

    chart.data.labels = hours.map((hour) => hour.label)
    chart.data.datasets[0].data = hours.map((hour) => hour.count)
    chart.options.plugins.title.text = this.hourlyChartTitle()
    chart.update("none")
  }

  barOptions(title) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: title,
          color: "#153243",
          font: { family: "Montserrat", size: 16, weight: "700" }
        }
      },
      scales: {
        x: {
          ticks: { color: "#696969", maxRotation: 45, minRotation: 0 },
          grid: { display: false }
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#696969" },
          grid: { color: "rgba(0,0,0,0.06)" }
        }
      }
    }
  }

  lineOptions(title) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: title,
          color: "#153243",
          font: { family: "Montserrat", size: 16, weight: "700" }
        }
      },
      scales: {
        x: {
          ticks: {
            color: "#696969",
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8
          },
          grid: { display: false }
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#696969" },
          grid: { color: "rgba(0,0,0,0.06)" }
        }
      }
    }
  }

  parseJson(value) {
    try {
      return JSON.parse(value || "{}")
    } catch (_error) {
      return {}
    }
  }
}
